import { Box, Typography, Button, TextField } from "@mui/material";
import { ChangeEvent, FormEvent, forwardRef, useState } from "react";
import WaitingListBG from "../../assets/images/Home/WaitingListBG.png";
import { joinWaitingList } from "../../api/home";
import { MuiTelInput } from "mui-tel-input";
import { useTranslation } from "react-i18next";

const WaitingListSection = forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>((props, ref) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const [isEmailActive, setIsEmailActive] = useState<boolean>(true);
  const [formData, setFormData] = useState({
    inputField: "",
  });
  const [inputFieldError, setInputFieldError] = useState("");

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ inputField: value });
    setInputFieldError("");
  };

  const validateForm = () => {
    let valid = true;

    let newError = "";

    if (!isEmailActive && !formData.inputField) {
      newError = t("pages.home.phoneNumberRequired");
      valid = false;
    }
    if (isEmailActive && (!formData.inputField || !/\S+@\S+\.\S+/.test(formData.inputField))) {
      newError = t("pages.home.validEmailRequired");
      valid = false;
    }
    setInputFieldError(newError);
    return valid;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const requestBody = {
        [isEmailActive ? "email" : "phoneNumber"]: formData.inputField,
      };
      const result = await joinWaitingList(requestBody);
    } catch (error: any) {}
  };

  return (
    <Box
      ref={ref}
      component="section"
      sx={{
        padding: "60px 0",
        background: `url(${WaitingListBG})`,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
      }}
    >
      <Box
        component="div"
        sx={{
          minHeight: "506px",
          margin: "0 auto",
          width: "95%",
          maxWidth: "1240px",
          borderRadius: "50px",
          padding: {
            xs: "63px 8.5px",
            lg: "95px 0",
          },
          background: "rgba(0, 0, 0, 0.80)",
          display: "flex",
          flexDirection: "column",
          rowGap: "28px",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontFamily: "HeadingNowVarTrial",
            fontStyle: "normal",
            fontWeight: 750,
            lineHeight: "130%",
            textTransform: "uppercase",
            fontSize: {
              xs: "35px",
              lg: "40px",
            },
            background: "linear-gradient(180deg, #FFF 0%, #B8C2CE 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            textAlign: "center",
          }}
        >
          {t("pages.home.joinWaitingList")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", rowGap: "10px" }}>
          <Typography
            component="h4"
            sx={{
              color: "#FFF",
              textAlign: "center",
              fontFamily: "Montserrat",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "32px",

              fontSize: {
                xs: "20px",
                lg: "24px",
              },
            }}
          >
            {t("pages.home.kickstartSuccess")}
          </Typography>
          <Typography
            component="p"
            sx={{
              color: "var(--Secondary-Mint, #BAEDBD)",
              textAlign: "center",
              fontFamily: "Montserrat",
              fontSize: "28px",
              fontStyle: "normal",
              fontWeight: 850,
              lineHeight: "120%",
              textTransform: "uppercase",
            }}
          >
            {t("pages.home.firstTenSignUps")}
          </Typography>
        </Box>
        <Box
          sx={{
            borderRadius: "50px",
            border: "1px solid  #1E2021",
            background: "rgba(28, 28, 28, 0.80)",
            overflow: "hidden",
            position: "relative",
            display: "flex",
            columnGap: "21px",
          }}
        >
          <Box
            sx={{
              background: "#FFF",
              borderRadius: "50px",
              width: currentLanguage === "en" ? "81px" : "127px",
              height: "100%",
              position: "absolute",
              top: "0",
              left: isEmailActive ? "0" : currentLanguage === "ar" ? "145px" : "93.25px",
              transition: "left, right 0.3s ease",
            }}
          ></Box>
          <Button
            onClick={() => {
              setIsEmailActive(true);
              setInputFieldError("");
              setFormData({ inputField: "" });
            }}
            disabled={isEmailActive}
            sx={{
              fontFamily: "HeadingNowVarTrial",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "normal",
              background: "transparent",
              border: "none",
              borderRadius: "50px",
              padding: "10px 16px 10px 26px",
              textAlign: "center",
              color: isEmailActive ? "#1C1C1C" : "#FFF",
              position: "relative",
              "&.Mui-disabled": {
                color: "#1C1C1C",
              },
            }}
          >
            {t("pages.home.email")}
          </Button>
          <Button
            disabled={!isEmailActive}
            onClick={() => {
              setIsEmailActive(false);
              setInputFieldError("");
              setFormData({ inputField: "" });
            }}
            sx={{
              fontFamily: "HeadingNowVarTrial",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "normal",
              background: "transparent",
              border: "none",
              borderRadius: "50px",
              padding: "10px 26px 10px 16px",
              textAlign: "center",
              color: isEmailActive ? "#FFF" : "#1C1C1C",
              position: "relative",
              "&.Mui-disabled": {
                color: "#1C1C1C",
              },
            }}
          >
            {t("pages.home.mobile")}
          </Button>
        </Box>
        <Box sx={{ position: "relative", maxWidth: "100%" }} component="form" onSubmit={handleSubmit}>
          {isEmailActive ? (
            <TextField
              name="inputField"
              autoComplete="off"
              onChange={handleChange}
              inputProps={{
                form: {
                  autoComplete: "off",
                },
              }}
              placeholder={t("pages.home.enterEmail")}
              sx={{
                fontFamily: "Montserrat",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "20px",
                color: "#A0A0A0",
                width: "492px",
                maxWidth: "100%",
                "& .MuiInputBase-root": {
                  border: "1px solid #1E2021",
                  background: "rgba(28, 28, 28, 0.80)",

                  padding: "0 14px",
                },
                "& .MuiInputBase-input": {
                  padding: "10px 115px 10px 0",
                  color: "#FFF",
                },
              }}
              helperText={inputFieldError}
              error={!!inputFieldError}
            />
          ) : (
            <MuiTelInput
              name="phoneNumber"
              value={formData.inputField}
              onChange={(newValue: string) => {
                setFormData({ inputField: newValue });
                setInputFieldError("");
              }}
              sx={{
                //   fontFamily: "Inter",
                fontFamily: "Almarai",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "normal",
                color: "#A0A0A0",
                width: "492px",
                maxWidth: "100%",
                direction: "ltr !important",
                "& .MuiInputBase-root": {
                  border: "1px solid #1E2021",
                  background: "rgba(28, 28, 28, 0.80)",

                  padding: "0 14px",
                },
                "& .MuiInputBase-input": {
                  padding: "10px 115px 10px 0",
                  color: "#FFF",
                },
              }}
              defaultCountry="SA"
              error={!!inputFieldError}
              helperText={inputFieldError}
            />
          )}

          <Button
            variant="primary-md"
            sx={{
              width: "112px",
              position: "absolute",
              right: "0",
              top: "0",
              height: "45.5px",
              background: "var(--Green---100, #008B07) !important",
              color: "var(--Base-White, #FFF)",
              fontFamily: "HeadingNowVarTrial",
              fontSize: "14px",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "20px",
            }}
            type="submit"
          >
            {t("pages.home.send")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
});

export default WaitingListSection;
